import { useState } from 'react';
import { useIsMobile, useSnackbar } from 'src/hooks';
import { ArrowBackIcon, ExplorerPlusButtons, SettingsLinkGroup, Box, Typography, Divider, Link, 
  PrivacyRadioGroup, Switch, Loader } from "src/components";
import { useAuth, logoutUser, toggleNewsletterSubscription, createStripePortalSession } from "src/utils";
import MembershipsModal from 'src/pages/memberships/memberships-modal';

export default function AccountSettings({ handleCurrentViewChange, handleSuccess }) {
  const [loadingStripePortal, setLoadingStripePortal] = useState(false);
  const [displayMembershipsModal, setDisplayMembershipsModal] = useState(false);

  const { user, setUser, logout } = useAuth();
  const { openSnackbar } = useSnackbar();
  const isMobile = useIsMobile();

  const isMembershipsEnabled = import.meta.env.VITE_FEATURE_MEMBERSHIPS === 'true';

  const handleNewsletterSubscriptionClick = async () => {
    try {
      await toggleNewsletterSubscription(!user?.isSubscribedToNewsletter);
      setUser((prevUser) => ({
        ...prevUser,
        isSubscribedToNewsletter: !prevUser.isSubscribedToNewsletter
      }));
      handleSuccess("Newsletter subscription updated");
    } catch (err) {
      console.error("Newsletter subscription toggle failed", err);
      openSnackbar("Failed to update newsletter subscription", "error");
    }
  };

  const handleLogoutClick = async () => {
    logout(); 
    handleSuccess("Logged out");
    try {
      await logoutUser();
    } catch (err) {
      console.error('Logout in backend failed', err);
    }
  };

  const handleManageSubscriptionClick = async () => {
    if (user.membership === "Free") {
      setDisplayMembershipsModal(true);
      return;
    }

    setLoadingStripePortal(true);
    try {
      const { url } = await createStripePortalSession(user.id);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Failed to open subscription portal", err);
      openSnackbar("Unable to open subscription management right now.", "error");
    } finally {
      setLoadingStripePortal(false);
    }
  };

  return (
    <Box sx={{paddingBottom: 2}}>

      <Box 
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          {/* Back Icon on the Left */}
          <Link 
            onClick={() => handleCurrentViewChange("AccountSummary")} 
            sx={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-start', 
              width: 40 // fixed width to help center title
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </Link>

          {/* Centered Title */}
          <Typography 
            variant="h6" 
            fontWeight={600} 
            sx={{ textAlign: 'center', flexGrow: 1 }}
          >
            Settings
          </Typography>

          {/* Empty spacer to balance left side */}
          <Box sx={{ width: 40 }} />
        </Box>

        <Divider sx={{ mt: 1 }} />

        {/* Explorer Plus Buttons */}
        <ExplorerPlusButtons />

        <PrivacyRadioGroup />

        <SettingsLinkGroup
          title="Account"
          items={[
            ...(isMembershipsEnabled
              ? [
                  {
                    label: 'Manage Subscription',
                    action: handleManageSubscriptionClick,
                    showArrow: true,
                  },
                ]
              : []),
            { label: 'Log Out', action: handleLogoutClick },
          ]}
        />

        <Typography variant="h6" mt={3} mb={1}>Manage Notifications</Typography>
        <Box sx={{
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'}}>

            <Typography variant={isMobile ? "subtitle1" : "body2"} fontWeight={500} color={"text.primary"}>
                Explorer Newsletter
            </Typography>
            <Switch 
                checked={user?.isSubscribedToNewsletter} 
                size="small" 
                onChange={() => handleNewsletterSubscriptionClick()} />
        </Box>


        <SettingsLinkGroup
          title="Support"
          items={[
            { label: 'Contact Us', href: '/contact-us' },
            { label: 'Terms & Conditions', href: '/terms-of-service' },
            { label: 'Privacy Policy', href: '/privacy-policy' },
            { label: 'Delete Account', href: '/delete-account', color: 'warning' },
          ]}
        />

        <Box sx={{ mt: 2, mb: isMobile ? 8 : 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            © {new Date().getFullYear()} PanAmExplorer. All rights reserved.
          </Typography>
        </Box>

        {loadingStripePortal && <Loader />}

        {displayMembershipsModal && (
            <MembershipsModal
                open={displayMembershipsModal}
                onClose={() => setDisplayMembershipsModal(false)}
            />
        )}

    </Box>
  );
}
